import { computed, ref, Ref } from 'vue';
import { getAddressSystemLogo } from '@/helpers/getAddressSystemLogo';
import { useAddressBookStore } from '@/store/addressBook';
import { shortAddress } from '@/helpers/shortAddress';
import { useAppStore } from '@/store/app';
import {
  AirdropFetchersResult,
  assertAddressSystem,
  compareUsdValue,
  getAddressSystem,
  NetworkIdType,
  TokenInfo,
} from '@sonarwatch/portfolio-core';
import { airdropsFetch } from '@/api/apiMain';
import runInBatch from '@/helpers/runInBatch';
import { sleep } from '@/helpers/sleep';
import { AirdropViewMode } from '@/types';
import { event } from 'vue-gtag';
import { useDataStore } from '@/store/data';
import { twMerge } from 'tailwind-merge';

export default function useAirdropChecker() {
  const addressBookStore = useAddressBookStore();
  const dataStore = useDataStore();
  const appStore = useAppStore();
  const maxAddresses = 12;

  const isCheckDone = ref(false);

  const cellClass = function (className?: string) {
    return twMerge(className, 'flex flex-row items-center min-h-[30px] my-2');
  };

  const addressSelectItems: Ref<Array<any>> = computed(() => {
    const items1: any[] = addressBookStore.items.map((i) => ({
      type: 'address',
      title: i.name ? i.name : shortAddress(i.address),
      value: i.address,
      address: i.address,
      name: i.name,
      image: getAddressSystemLogo(i.address),
    }));
    const items2: any[] = addressBookStore.bundles.map((b) => ({
      type: 'bundle',
      title: b.name,
      value: b.addresses.split('_').join('\n'),
      icon: 'mdi-package-variant-closed',
    }));
    if (items1.length > 0 && items2.length > 0) {
      items2.unshift({ type: 'divider' });
    }
    return [...items1, ...items2];
  });

  const mode: Ref<AirdropViewMode> = ref(AirdropViewMode.address);
  const now: Ref<number> = ref(Date.now());
  const isLoading: Ref<boolean> = ref(false);
  const results: Ref<AirdropFetchersResult[]> = ref([]);
  const inputErrorMessages: Ref<string[]> = ref([]);
  const selectAirdropViewItems: Ref<
    {
      value: string;
      image: string;
      name: string | undefined;
      emitterName: string;
      title: string;
      claimStart: number | undefined;
      claimEnd: number | undefined;
    }[]
  > = ref([]);
  const selectAddressViewItems: Ref<string[]> = ref([]);
  const addressBookSelect: Ref<string | null> = ref(null);
  const selectedAddress: Ref<string | null> = ref(null);
  const selectedAirdrop: Ref<string | null> = ref(null);
  const inputAddresses = computed(() => {
    if (appStore.airdropCheckInput === '') return [];
    const addresses = appStore.airdropCheckInput
      .split('\n')
      .map((a) => a.replace(/ /g, ''))
      .filter((v) => v !== '');

    return Array.from(new Set(addresses));
  });

  function toogleViewMode() {
    mode.value = (mode.value + 1) % 2;
  }

  function onImport() {
    if (
      !appStore.airdropCheckInput.endsWith('\n') &&
      appStore.airdropCheckInput !== ''
    )
      appStore.airdropCheckInput += '\n';
    appStore.airdropCheckInput += addressBookSelect.value;
    addressBookSelect.value = null;
  }

  async function onCheck() {
    // Verify
    if (inputAddresses.value.length < 1) {
      inputErrorMessages.value = ['Provide at least one address'];
      return;
    }
    // @disabled-jup
    const invalidAddress = inputAddresses.value.find(
      (a) => getAddressSystem(a) !== 'solana',
    );

    if (invalidAddress) {
      inputErrorMessages.value = [`${invalidAddress} is not a valid address`];
      return;
    }
    if (inputAddresses.value.length > maxAddresses) {
      inputErrorMessages.value = [
        `Addresses must be less than: ${maxAddresses}`,
      ];
      return;
    }
    event('button_click', {
      button_id: 'airdropchecker-check',
      n_addresses: inputAddresses.value.length,
    });
    inputErrorMessages.value = [];

    // Fetch
    isLoading.value = true;
    const start = Date.now();

    const settledResults = await runInBatch(
      inputAddresses.value.map((a) => {
        return () => {
          const addressSystem = assertAddressSystem(a);
          return airdropsFetch(a, addressSystem).then((r) => {
            if (r.tokenInfo) {
              for (const [networkId, tokenInfoRecord] of Object.entries(
                r.tokenInfo,
              )) {
                if (tokenInfoRecord) {
                  dataStore.pushTokenInfos(
                    networkId as NetworkIdType,
                    new Map<string, TokenInfo>(Object.entries(tokenInfoRecord)),
                  );
                }
              }
            }
            return r;
          });
        };
      }),
      2,
      1000,
    ).finally(async () => {
      const end = Date.now();
      now.value = end;
      if (end - start < 2000) await sleep(1000);
      isLoading.value = false;
    });
    results.value = settledResults
      .map((r) => {
        if (r.status === 'fulfilled') {
          return { ...r.value };
        }
        return null;
      })
      .filter((r) => r !== null) as AirdropFetchersResult[];

    selectAddressViewItems.value = results.value.map((r) => r.owner);
    selectedAddress.value = selectAddressViewItems.value.at(0) || null;

    selectAirdropViewItems.value = getSelectAirdropViewItems();
    selectedAirdrop.value = selectAirdropViewItems.value.at(0)?.value || null;
    isCheckDone.value = true;
  }
  function getSelectAirdropViewItems() {
    const now = Date.now();
    if (results.value.length === 0) return [];
    const airdrops = results.value
      .map((r) =>
        r.airdrops.map((a) => ({
          value: a.id,
          image: a.image,
          name: a.name,
          emitterName: a.emitterName,
          title: a.emitterName,
          claimStart: a.claimStart,
          claimEnd: a.claimEnd,
        })),
      )
      .flat();
    return filterById(airdrops).sort((a, b) => {
      if (a.claimStart === undefined) {
        if (b.claimStart === undefined) return 1;
        return b.claimStart - now;
      }
      if (b.claimStart === undefined) return now - a.claimStart;
      return b.claimStart - a.claimStart;
    });
  }

  const page = computed(() => {
    if (mode.value === AirdropViewMode.address) {
      return (
        results.value.findIndex((r) => r.owner === selectedAddress.value) + 1
      );
    } else if (mode.value === AirdropViewMode.aidrop) {
      return (
        selectAirdropViewItems.value.findIndex(
          (n) => n.value === selectedAirdrop.value,
        ) + 1
      );
    }
    return 1;
  });
  const selectedAirdropInfo = computed(() => {
    const a = selectAirdropViewItems.value.find(
      (n) => n.value === selectedAirdrop.value,
    );
    return {
      claimStart: a?.claimStart,
      claimEnd: a?.claimEnd,
    };
  });
  function pageNext() {
    if (mode.value === AirdropViewMode.address) {
      selectedAddress.value =
        results.value.at(page.value % results.value.length)?.owner || null;
    } else if (mode.value === AirdropViewMode.aidrop) {
      selectedAirdrop.value =
        selectAirdropViewItems.value.at(
          page.value % selectAirdropViewItems.value.length,
        )?.value || null;
    }
  }
  function pagePrevious() {
    if (mode.value === AirdropViewMode.address) {
      selectedAddress.value =
        results.value.at((page.value - 2) % results.value.length)?.owner ||
        null;
    } else {
      selectedAirdrop.value =
        selectAirdropViewItems.value.at(
          (page.value - 2) % selectAirdropViewItems.value.length,
        )?.value || null;
    }
  }

  const addressViewItems = computed(() => {
    const items = results.value.at(page.value - 1)?.airdrops || [];
    const now = Date.now();
    items.sort((a, b) => {
      if (a.claimStart === undefined) {
        if (b.claimStart === undefined) return 1;
        return b.claimStart - now;
      }
      if (b.claimStart === undefined) return now - a.claimStart;
      return b.claimStart - a.claimStart;
    });
    return items;
  });
  const airdropViewItems = computed(() => {
    return results.value
      .map((r) => r.airdrops)
      .flat()
      .filter(
        (a) => a.id === selectAirdropViewItems.value[page.value - 1]?.value,
      )
      .sort((a, b) => {
        return compareUsdValue(a.value, b.value);
      });
  });

  const fetcherFailedIds = computed(() => {
    return [
      ...new Set(
        results.value
          .map((r) => r.fetcherReports)
          .flat()
          .filter((a) => a.status === 'failed')
          .map((a) => a.id),
      ),
    ];
  });

  function filterById<T extends { value: string }>(array: T[]) {
    const encounteredIDs: Record<string, boolean> = {};
    return array.filter((obj) => {
      if (encounteredIDs[obj.value]) return false;
      encounteredIDs[obj.value] = true;
      return true;
    });
  }

  function onTryDemo() {
    appStore.airdropCheckInput =
      '0xcfa90668e446e80d0172ec7e9f9f0e80d48045bbb1817616552060574e049866\ntEsT1vjsJeKHw9GH5HpnQszn2LWmjR6q1AVCDCj51nd';
    onCheck();
  }

  return {
    mode,
    isLoading,
    maxAddresses,
    inputAddresses,
    inputErrorMessages,
    addressBookSelect,
    addressSelectItems,
    onImport,
    onCheck,
    page,
    pageNext,
    pagePrevious,
    addressViewItems,
    selectAddressViewItems,
    selectAirdropViewItems,
    fetcherFailedIds,
    selectedAddress,
    selectedAirdrop,
    toogleViewMode,
    results,
    cellClass,
    airdropViewItems,
    selectedAirdropInfo,
    isCheckDone,
    onTryDemo,
  };
}
